import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { IOnboardingTooltipItem } from "../../models/onboarding-tooltip";
import { AppState } from "../../store/root-reducer";
import KuikaOnboardingTooltip from "../ui-kit/kuika-onboarding-tooltip/kuika-onboarding-tooltip";
import KuikaSvg from "../ui-kit/kuika-svg/kuika-svg";
import { KUIKA_TOOLTIP_DELAY_MS, KUIKA_TOOLTIP_SKIP_DELAY_MS } from "../ui-kit/kuika-tooltip/kuika-tooltip-provider";
import { AnimatedIconType, OnboardingAnimatedIcon } from "./onboarding-animated-icon";
import { useOnboardingTooltipMap } from "./onboarding-tooltip-query";
import "./onboarding-tooltip-layer.scss";

interface TooltipPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface ActiveTooltip {
  tooltip: IOnboardingTooltipItem;
  element: HTMLElement;
  position: TooltipPosition;
  currentStepIndex: number;
}

interface IconPosition {
  tooltip: IOnboardingTooltipItem;
  element: HTMLElement;
  position: TooltipPosition;
}

const POSITION_THRESHOLD = 0.5;

const hasPositionChanged = (oldPos: TooltipPosition, newPos: TooltipPosition): boolean => {
  return (
    Math.abs(oldPos.top - newPos.top) > POSITION_THRESHOLD ||
    Math.abs(oldPos.left - newPos.left) > POSITION_THRESHOLD ||
    Math.abs(oldPos.width - newPos.width) > POSITION_THRESHOLD ||
    Math.abs(oldPos.height - newPos.height) > POSITION_THRESHOLD
  );
};

// Grace that lets the pointer cross the gap from a trigger onto interactive content (learn-more
// link, step buttons). Short on purpose: sweeping across cards must not leave popups behind.
export const CLOSE_DELAY_DURATION: number = 150;
// Grace that lets the pointer travel from a card onto its "i" badge.
export const ICON_HIDE_DELAY: number = 100;
// A tooltip opened straight from a card (no badge) waits like every other designer tooltip, so a
// fast sweep opens nothing; once one has been open, neighbours open at once.
export const OPEN_DELAY_DURATION: number = KUIKA_TOOLTIP_DELAY_MS;
export const SKIP_OPEN_DELAY_DURATION: number = KUIKA_TOOLTIP_SKIP_DELAY_MS;

const hasTourSteps = (tooltip: IOnboardingTooltipItem): boolean => !!tooltip.steps && tooltip.steps.length > 0;

const hasInteractiveContent = (tooltip: IOnboardingTooltipItem): boolean =>
  !!tooltip.learnMoreUrl || hasTourSteps(tooltip);

// "hint" and "always" badges are permanent fixtures; only their popup comes and goes.
const keepsIconWhenClosed = (tooltip: IOnboardingTooltipItem): boolean =>
  tooltip.triggerType === "always" || tooltip.triggerType === "hint";

const selectVisibleTooltips = (state: AppState): Map<string, HTMLElement> => state.onboardingTooltip.visibleTooltips;

const GlobalOnboardingTooltipLayer = (): React.ReactPortal | null => {
  const tooltipMap: Map<string, IOnboardingTooltipItem> = useOnboardingTooltipMap();
  const visibleTooltips: Map<string, HTMLElement> = useSelector(selectVisibleTooltips);
  const [activeTooltips, setActiveTooltips] = useState<Map<string, ActiveTooltip>>(new Map());
  const [visibleIcons, setVisibleIcons] = useState<Map<string, IconPosition>>(new Map());
  const eventListenersRef = useRef<Map<string, { element: HTMLElement; cleanup: () => void }>>(new Map());
  const rafRef = useRef<number | null>(null);
  const positionsRef = useRef<Map<string, TooltipPosition>>(new Map());
  const iconPositionsRef = useRef<Map<string, TooltipPosition>>(new Map());
  const hideTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const iconHideTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const isHoveringContentRef = useRef<Set<string>>(new Set());
  const isHoveringIconRef = useRef<Set<string>>(new Set());
  const isHoveringElementRef = useRef<Set<string>>(new Set());
  const openTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Popups and badges that exist only because of hover. At most one of each is kept: opening a
  // new one dismisses the rest immediately, instead of each waiting out its own close grace.
  const hoverTooltipsRef = useRef<Map<string, IOnboardingTooltipItem>>(new Map());
  const hoverIconIdsRef = useRef<Set<string>>(new Set());
  const lastHoverCloseAtRef = useRef<number>(Number.NEGATIVE_INFINITY);

  const updateTooltipPosition = useCallback((tooltipId: string, element: HTMLElement, currentStepIndex?: number) => {
    const rect = element.getBoundingClientRect();
    const newPosition: TooltipPosition = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height
    };

    const cachedPosition = positionsRef.current.get(tooltipId);
    if (cachedPosition && !hasPositionChanged(cachedPosition, newPosition)) {
      return;
    }

    positionsRef.current.set(tooltipId, newPosition);

    setActiveTooltips((prev) => {
      const existing = prev.get(tooltipId);
      if (!existing) return prev;

      const newMap = new Map(prev);
      newMap.set(tooltipId, {
        ...existing,
        position: newPosition,
        currentStepIndex: currentStepIndex !== undefined ? currentStepIndex : existing.currentStepIndex
      });
      return newMap;
    });
  }, []);

  const updateIconPosition = useCallback((tooltipId: string, element: HTMLElement, tooltip: IOnboardingTooltipItem) => {
    const rect = element.getBoundingClientRect();
    const newPosition: TooltipPosition = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height
    };

    const cachedPosition = iconPositionsRef.current.get(tooltipId);
    if (cachedPosition && !hasPositionChanged(cachedPosition, newPosition)) {
      return;
    }

    iconPositionsRef.current.set(tooltipId, newPosition);

    setVisibleIcons((prev) => {
      const newMap = new Map(prev);
      newMap.set(tooltipId, {
        tooltip,
        element,
        position: newPosition
      });
      return newMap;
    });
  }, []);

  const removeIcon = useCallback((tooltipId: string) => {
    iconPositionsRef.current.delete(tooltipId);
    setVisibleIcons((prev) => {
      if (!prev.has(tooltipId)) return prev;
      const newMap = new Map(prev);
      newMap.delete(tooltipId);
      return newMap;
    });
  }, []);

  const showIcon = useCallback(
    (tooltip: IOnboardingTooltipItem, element: HTMLElement) => {
      const existingTimeout = iconHideTimeoutsRef.current.get(tooltip.id);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        iconHideTimeoutsRef.current.delete(tooltip.id);
      }
      updateIconPosition(tooltip.id, element, tooltip);
    },
    [updateIconPosition]
  );

  const clearIconHideTimeout = useCallback((tooltipId: string) => {
    const existingTimeout = iconHideTimeoutsRef.current.get(tooltipId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      iconHideTimeoutsRef.current.delete(tooltipId);
    }
  }, []);

  const findElementForStep = useCallback((tooltip: IOnboardingTooltipItem, stepIndex: number): HTMLElement | null => {
    const steps = tooltip.steps;
    if (!steps || steps.length === 0) return null;

    const step = steps[stepIndex];
    let stepSelector = step?.selector;

    if (!stepSelector && stepIndex === 0) {
      stepSelector = tooltip.selector;
    }

    if (!stepSelector) return null;

    let element: HTMLElement | null = null;
    if (tooltip.useCssSelector) {
      try {
        const cssSelector = tooltip.selector?.startsWith(".") ? tooltip.selector : `.${tooltip.selector}`;
        element = document.querySelector(cssSelector) as HTMLElement;
      } catch (e) {
        console.warn(`Invalid CSS selector: ${stepSelector}`, e);
        return null;
      }
    } else {
      element = document.querySelector(`[data-onboarding-id="${stepSelector}"]`) as HTMLElement;
      if (!element) {
        element = document.querySelector(`[id="${stepSelector}"]`) as HTMLElement;
      }
    }

    return element;
  }, []);

  const showTooltip = useCallback((tooltip: IOnboardingTooltipItem, element: HTMLElement, stepIndex: number = 0) => {
    const rect = element.getBoundingClientRect();
    const position: TooltipPosition = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height
    };

    positionsRef.current.set(tooltip.id, position);

    setActiveTooltips((prev) => {
      const newMap = new Map(prev);
      newMap.set(tooltip.id, {
        tooltip,
        element,
        position,
        currentStepIndex: stepIndex
      });
      return newMap;
    });
  }, []);

  const hideTooltip = useCallback(
    (tooltip: IOnboardingTooltipItem) => {
      const tooltipId = tooltip.id;
      positionsRef.current.delete(tooltipId);
      isHoveringContentRef.current.delete(tooltipId);
      isHoveringIconRef.current.delete(tooltipId);
      isHoveringElementRef.current.delete(tooltipId);

      const existingTimeout = hideTimeoutsRef.current.get(tooltipId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        hideTimeoutsRef.current.delete(tooltipId);
      }
      const existingIconTimeout = iconHideTimeoutsRef.current.get(tooltipId);
      if (existingIconTimeout) {
        clearTimeout(existingIconTimeout);
        iconHideTimeoutsRef.current.delete(tooltipId);
      }
      const pendingOpen = openTimeoutsRef.current.get(tooltipId);
      if (pendingOpen) {
        clearTimeout(pendingOpen);
        openTimeoutsRef.current.delete(tooltipId);
      }
      if (hoverTooltipsRef.current.delete(tooltipId)) {
        lastHoverCloseAtRef.current = Date.now();
      }

      setActiveTooltips((prev) => {
        if (!prev.has(tooltipId)) return prev;
        const newMap = new Map(prev);
        newMap.delete(tooltipId);
        return newMap;
      });

      if (keepsIconWhenClosed(tooltip)) {
        return;
      }

      hoverIconIdsRef.current.delete(tooltipId);
      removeIcon(tooltipId);
    },
    [removeIcon]
  );

  const scheduleHideIcon = useCallback(
    (tooltipId: string) => {
      const existingTimeout = iconHideTimeoutsRef.current.get(tooltipId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      const timeout = setTimeout(() => {
        if (
          !isHoveringIconRef.current.has(tooltipId) &&
          !isHoveringElementRef.current.has(tooltipId) &&
          !isHoveringContentRef.current.has(tooltipId)
        ) {
          setActiveTooltips((currentTooltips) => {
            if (!currentTooltips.has(tooltipId)) {
              removeIcon(tooltipId);
            }
            return currentTooltips;
          });
        }
        iconHideTimeoutsRef.current.delete(tooltipId);
      }, ICON_HIDE_DELAY);
      iconHideTimeoutsRef.current.set(tooltipId, timeout);
    },
    [removeIcon]
  );

  const clearHideTimeout = useCallback((tooltipId: string) => {
    const existingTimeout = hideTimeoutsRef.current.get(tooltipId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      hideTimeoutsRef.current.delete(tooltipId);
    }
  }, []);

  const scheduleHideTooltip = useCallback(
    (tooltip: IOnboardingTooltipItem) => {
      const tooltipId = tooltip.id;
      clearHideTimeout(tooltipId);
      const timeout = setTimeout(() => {
        if (!isHoveringContentRef.current.has(tooltipId)) {
          hideTooltip(tooltip);
        }
        hideTimeoutsRef.current.delete(tooltipId);
      }, CLOSE_DELAY_DURATION);
      hideTimeoutsRef.current.set(tooltipId, timeout);
    },
    [clearHideTimeout, hideTooltip]
  );

  // Closes every hover-born popup and badge except `keepId`, with no grace: the pointer has
  // clearly moved on to another target.
  const dismissOtherHoverSurfaces = useCallback(
    (keepId: string) => {
      hoverTooltipsRef.current.forEach((tooltip, tooltipId) => {
        if (tooltipId !== keepId) {
          hideTooltip(tooltip);
        }
      });
      Array.from(hoverIconIdsRef.current).forEach((tooltipId) => {
        if (tooltipId === keepId || hoverTooltipsRef.current.has(tooltipId)) return;
        clearIconHideTimeout(tooltipId);
        hoverIconIdsRef.current.delete(tooltipId);
        removeIcon(tooltipId);
      });
    },
    [hideTooltip, clearIconHideTimeout, removeIcon]
  );

  const openHoverTooltip = useCallback(
    (tooltip: IOnboardingTooltipItem, element: HTMLElement) => {
      dismissOtherHoverSurfaces(tooltip.id);
      hoverTooltipsRef.current.set(tooltip.id, tooltip);
      showTooltip(tooltip, element, 0);
    },
    [dismissOtherHoverSurfaces, showTooltip]
  );

  const cancelPendingOpen = useCallback((tooltipId: string) => {
    const pendingOpen = openTimeoutsRef.current.get(tooltipId);
    if (pendingOpen) {
      clearTimeout(pendingOpen);
      openTimeoutsRef.current.delete(tooltipId);
    }
  }, []);

  const handleContentMouseEnter = useCallback(
    (tooltipId: string) => {
      isHoveringContentRef.current.add(tooltipId);
      clearHideTimeout(tooltipId);
      clearIconHideTimeout(tooltipId);
    },
    [clearHideTimeout, clearIconHideTimeout]
  );

  const handleContentMouseLeave = useCallback(
    (tooltip: IOnboardingTooltipItem) => {
      const tooltipId = tooltip.id;
      isHoveringContentRef.current.delete(tooltipId);

      if (tooltip.triggerType === "hover" || tooltip.triggerType === "hint" || tooltip.triggerType === "always") {
        scheduleHideTooltip(tooltip);
      }
    },
    [scheduleHideTooltip]
  );

  const setupEventListeners = useCallback(() => {
    const currentListeners = eventListenersRef.current;
    const newListenersNeeded = new Set<string>();

    visibleTooltips.forEach((element, selector) => {
      const tooltip = tooltipMap.get(selector);
      if (!tooltip || tooltip.triggerType === "auto") return;

      // KOA-16987: skip elements that already have a KuikaTooltip wrapper.
      // Otherwise both tooltip systems fire on the same hover/click, producing
      // two tooltips simultaneously.
      if (element.getAttribute("data-has-kuika-tooltip") === "true") return;

      newListenersNeeded.add(selector);

      if (currentListeners.has(selector)) {
        const existing = currentListeners.get(selector)!;
        if (existing.element === element) {
          return;
        }
        existing.cleanup();
        currentListeners.delete(selector);
      }

      if (tooltip.triggerType === "hint" || tooltip.triggerType === "always") {
        newListenersNeeded.add(selector);

        if (currentListeners.has(selector)) {
          const existing = currentListeners.get(selector)!;
          if (existing.element === element) {
            return;
          }
          existing.cleanup();
          currentListeners.delete(selector);
        }

        if (tooltip.icon) {
          showIcon(tooltip, element);
          // For always visible icons, we still want to show tooltip on hover of the icon
          // This is handled by the icon's own mouseEnter/Leave handlers
          // We just need to register cleanup
          currentListeners.set(selector, {
            element,
            cleanup: () => {
              removeIcon(tooltip.id);
            }
          });
        } else {
          showTooltip(tooltip, element, 0);
          currentListeners.set(selector, {
            element,
            cleanup: () => {
              hideTooltip(tooltip);
            }
          });
        }
        return;
      }

      if (tooltip.icon) {
        const handleElementMouseEnter = () => {
          isHoveringElementRef.current.add(tooltip.id);
          clearIconHideTimeout(tooltip.id);
          dismissOtherHoverSurfaces(tooltip.id);
          hoverIconIdsRef.current.add(tooltip.id);
          showIcon(tooltip, element);
        };

        const handleElementMouseLeave = () => {
          isHoveringElementRef.current.delete(tooltip.id);
          scheduleHideIcon(tooltip.id);
        };

        element.addEventListener("mouseenter", handleElementMouseEnter);
        element.addEventListener("mouseleave", handleElementMouseLeave);

        currentListeners.set(selector, {
          element,
          cleanup: () => {
            element.removeEventListener("mouseenter", handleElementMouseEnter);
            element.removeEventListener("mouseleave", handleElementMouseLeave);
          }
        });
        return;
      }

      const handleMouseEnter = () => {
        if (tooltip.triggerType !== "hover") return;
        clearHideTimeout(tooltip.id);
        if (hoverTooltipsRef.current.has(tooltip.id)) return;

        const isWarm =
          hoverTooltipsRef.current.size > 0 || Date.now() - lastHoverCloseAtRef.current < SKIP_OPEN_DELAY_DURATION;
        if (isWarm) {
          openHoverTooltip(tooltip, element);
          return;
        }
        cancelPendingOpen(tooltip.id);
        openTimeoutsRef.current.set(
          tooltip.id,
          setTimeout(() => {
            openTimeoutsRef.current.delete(tooltip.id);
            openHoverTooltip(tooltip, element);
          }, OPEN_DELAY_DURATION)
        );
      };

      const handleMouseLeave = () => {
        if (tooltip.triggerType !== "hover") return;
        cancelPendingOpen(tooltip.id);
        if (hasInteractiveContent(tooltip)) {
          scheduleHideTooltip(tooltip);
        } else {
          hideTooltip(tooltip);
        }
      };

      // A press closes a hover popup, as a kit tooltip does: the pointer usually stays on a trigger
      // that opens a popup of its own (the action picker's "+"), and the hint would sit on top of it.
      const handlePointerDown = (): void => {
        if (tooltip.triggerType !== "hover") return;
        hideTooltip(tooltip); // also drops a pending open
      };

      const handleClick = (e: MouseEvent) => {
        if (tooltip.triggerType === "click") {
          e.stopPropagation();
          setActiveTooltips((prev) => {
            const newMap = new Map(prev);
            if (newMap.has(tooltip.id)) {
              positionsRef.current.delete(tooltip.id);
              newMap.delete(tooltip.id);
            } else {
              const rect = element.getBoundingClientRect();
              const position: TooltipPosition = {
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height
              };
              positionsRef.current.set(tooltip.id, position);
              newMap.set(tooltip.id, {
                tooltip,
                element,
                position,
                currentStepIndex: 0
              });
            }
            return newMap;
          });
        }
      };

      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);
      element.addEventListener("pointerdown", handlePointerDown);
      element.addEventListener("click", handleClick);

      currentListeners.set(selector, {
        element,
        cleanup: () => {
          element.removeEventListener("mouseenter", handleMouseEnter);
          element.removeEventListener("mouseleave", handleMouseLeave);
          element.removeEventListener("pointerdown", handlePointerDown);
          element.removeEventListener("click", handleClick);
          cancelPendingOpen(tooltip.id);
        }
      });
    });

    currentListeners.forEach((listener, selector) => {
      if (!newListenersNeeded.has(selector)) {
        listener.cleanup();
        currentListeners.delete(selector);
        const tooltip = tooltipMap.get(selector);
        if (tooltip) {
          hideTooltip(tooltip);
          removeIcon(tooltip.id);
        }
      }
    });
  }, [
    visibleTooltips,
    tooltipMap,
    showTooltip,
    hideTooltip,
    clearHideTimeout,
    scheduleHideTooltip,
    showIcon,
    scheduleHideIcon,
    clearIconHideTimeout,
    removeIcon,
    dismissOtherHoverSurfaces,
    openHoverTooltip,
    cancelPendingOpen
  ]);

  useEffect(() => {
    setupEventListeners();
  }, [setupEventListeners]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      activeTooltips.forEach((activeTooltip) => {
        const { tooltip } = activeTooltip;

        if (tooltip.triggerType !== "click") return;

        const tooltipElement = document.querySelector(`[data-tooltip-id="${tooltip.id}"]`);
        const iconElement = document.querySelector(`[data-icon-id="${tooltip.id}"]`);

        // Also check if click is inside the actual tooltip content (rendered via Portal)
        // The tooltip content has class 'kuika-onboarding-tooltip__content'
        const isClickInsideTooltipContent = target.closest(".kuika-onboarding-tooltip__content") !== null;

        const isClickInsideTooltip = tooltipElement?.contains(target);
        const isClickInsideIcon = iconElement?.contains(target);

        if (!isClickInsideTooltip && !isClickInsideIcon && !isClickInsideTooltipContent) {
          hideTooltip(tooltip);
        }
      });
    };

    if (activeTooltips.size > 0) {
      document.addEventListener("mousedown", handleOutsideClick);
      return () => {
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    }
  }, [activeTooltips, hideTooltip]);

  useEffect(() => {
    return () => {
      eventListenersRef.current.forEach((listener) => listener.cleanup());
      eventListenersRef.current.clear();
      hideTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      hideTimeoutsRef.current.clear();
      iconHideTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      iconHideTimeoutsRef.current.clear();
      openTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      openTimeoutsRef.current.clear();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (activeTooltips.size === 0 && visibleIcons.size === 0) {
      positionsRef.current.clear();
      iconPositionsRef.current.clear();
      return;
    }

    let isRunning = true;

    const updatePositions = () => {
      if (!isRunning) return;

      activeTooltips.forEach((activeTooltip, id) => {
        if (activeTooltip.element.isConnected) {
          updateTooltipPosition(id, activeTooltip.element, activeTooltip.currentStepIndex);
        }
      });

      visibleIcons.forEach((iconData, id) => {
        if (iconData.element.isConnected) {
          updateIconPosition(id, iconData.element, iconData.tooltip);
        }
      });

      rafRef.current = requestAnimationFrame(updatePositions);
    };

    rafRef.current = requestAnimationFrame(updatePositions);

    return () => {
      isRunning = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [activeTooltips, visibleIcons, updateTooltipPosition, updateIconPosition]);

  const handleStepChange = useCallback(
    (tooltip: IOnboardingTooltipItem, newStepIndex: number) => {
      const activeTooltip = activeTooltips.get(tooltip.id);
      if (!activeTooltip) return;

      const newElement = findElementForStep(tooltip, newStepIndex);
      if (newElement) {
        showTooltip(tooltip, newElement, newStepIndex);
      } else {
        setActiveTooltips((prev) => {
          const newMap = new Map(prev);
          const existing = newMap.get(tooltip.id);
          if (existing) {
            newMap.set(tooltip.id, {
              ...existing,
              currentStepIndex: newStepIndex
            });
          }
          return newMap;
        });
      }
    },
    [activeTooltips, findElementForStep, showTooltip]
  );

  const handleTooltipClose = useCallback(
    (tooltip: IOnboardingTooltipItem) => {
      hideTooltip(tooltip);
    },
    [hideTooltip]
  );

  const handleIconMouseEnter = useCallback(
    (tooltip: IOnboardingTooltipItem, iconElement: HTMLElement) => {
      isHoveringIconRef.current.add(tooltip.id);
      clearIconHideTimeout(tooltip.id);
      clearHideTimeout(tooltip.id);

      // The badge is already the deliberate target, so its popup opens without a delay.
      if (tooltip.triggerType === "hover" || tooltip.triggerType === "hint" || tooltip.triggerType === "always") {
        openHoverTooltip(tooltip, iconElement);
      }
    },
    [clearHideTimeout, clearIconHideTimeout, openHoverTooltip]
  );

  const handleIconMouseLeave = useCallback(
    (tooltip: IOnboardingTooltipItem) => {
      isHoveringIconRef.current.delete(tooltip.id);

      if (tooltip.triggerType === "hover" || tooltip.triggerType === "hint" || tooltip.triggerType === "always") {
        if (hasInteractiveContent(tooltip)) {
          scheduleHideTooltip(tooltip);
        } else {
          hideTooltip(tooltip);
        }
      } else {
        // if always visible
        scheduleHideIcon(tooltip.id);
      }
    },
    [scheduleHideTooltip, hideTooltip, scheduleHideIcon]
  );

  const handleIconClick = useCallback(
    (tooltip: IOnboardingTooltipItem, iconElement: HTMLElement, e: React.MouseEvent) => {
      if (tooltip.triggerType === "click") {
        e.stopPropagation();
        setActiveTooltips((prev) => {
          const newMap = new Map(prev);
          if (newMap.has(tooltip.id)) {
            positionsRef.current.delete(tooltip.id);
            newMap.delete(tooltip.id);
          } else {
            const rect = iconElement.getBoundingClientRect();
            const position: TooltipPosition = {
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height
            };
            positionsRef.current.set(tooltip.id, position);
            newMap.set(tooltip.id, {
              tooltip,
              element: iconElement,
              position,
              currentStepIndex: 0
            });
          }
          return newMap;
        });
      }
    },
    []
  );

  if (activeTooltips.size === 0 && visibleIcons.size === 0) {
    return null;
  }

  const tooltipElements: React.ReactNode[] = [];
  const iconElements: React.ReactNode[] = [];

  activeTooltips.forEach((activeTooltip) => {
    const { tooltip, position, currentStepIndex } = activeTooltip;
    const isInteractive: boolean = hasInteractiveContent(tooltip);

    tooltipElements.push(
      <div key={tooltip.id} data-tooltip-id={tooltip.id}>
        <KuikaOnboardingTooltip
          label={tooltip.label}
          header={tooltip.header}
          image={tooltip.image}
          steps={tooltip.steps}
          learnMoreUrl={tooltip.learnMoreUrl}
          position={tooltip.position}
          triggerType="auto"
          backgroundColor={tooltip.backgroundColor}
          textColor={tooltip.textColor}
          width={tooltip.width}
          height={tooltip.height}
          tooltipOffsetX={tooltip.tooltipOffsetX}
          tooltipOffsetY={tooltip.tooltipOffsetY}
          onClose={() => handleTooltipClose(tooltip)}
          onDone={() => handleTooltipClose(tooltip)}
          onContentMouseEnter={isInteractive ? () => handleContentMouseEnter(tooltip.id) : undefined}
          onContentMouseLeave={isInteractive ? () => handleContentMouseLeave(tooltip) : undefined}
          onStepChange={(stepIndex) => handleStepChange(tooltip, stepIndex)}
          initialStep={currentStepIndex}
        >
          <span
            className="onboarding-tooltip-layer__anchor"
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: position.width,
              height: position.height,
              pointerEvents: "none"
            }}
          />
        </KuikaOnboardingTooltip>
      </div>
    );
  });

  visibleIcons.forEach((iconData) => {
    const { tooltip, position } = iconData;
    const iconSize = tooltip.iconSize || 20;
    const iconColor = tooltip.iconColor || "var(--blue-6)";

    const getIconPosition = () => {
      const offset = 4;
      const targetRect = position;

      // If explicit icon position is set, use it
      if (tooltip.iconPosition) {
        switch (tooltip.iconPosition) {
          // TOP positions
          case "top-start":
            return {
              top: targetRect.top - iconSize - offset,
              left: targetRect.left
            };
          case "top-center":
            return {
              top: targetRect.top - iconSize - offset,
              left: targetRect.left + targetRect.width / 2 - iconSize / 2
            };
          case "top-end":
            return {
              top: targetRect.top - iconSize - offset,
              left: targetRect.left + targetRect.width - iconSize
            };
          // RIGHT positions
          case "right-start":
            return {
              top: targetRect.top,
              left: targetRect.left + targetRect.width + offset
            };
          case "right-center":
            return {
              top: targetRect.top + targetRect.height / 2 - iconSize / 2,
              left: targetRect.left + targetRect.width + offset
            };
          case "right-end":
            return {
              top: targetRect.top + targetRect.height - iconSize,
              left: targetRect.left + targetRect.width + offset
            };
          // BOTTOM positions
          case "bottom-start":
            return {
              top: targetRect.top + targetRect.height + offset,
              left: targetRect.left
            };
          case "bottom-center":
            return {
              top: targetRect.top + targetRect.height + offset,
              left: targetRect.left + targetRect.width / 2 - iconSize / 2
            };
          case "bottom-end":
            return {
              top: targetRect.top + targetRect.height + offset,
              left: targetRect.left + targetRect.width - iconSize
            };
          // LEFT positions
          case "left-start":
            return {
              top: targetRect.top,
              left: targetRect.left - iconSize - offset
            };
          case "left-center":
            return {
              top: targetRect.top + targetRect.height / 2 - iconSize / 2,
              left: targetRect.left - iconSize - offset
            };
          case "left-end":
            return {
              top: targetRect.top + targetRect.height - iconSize,
              left: targetRect.left - iconSize - offset
            };
        }
      }

      // Fallback to legacy behavior based on tooltip position
      switch (tooltip.position) {
        case "top":
          return {
            top: targetRect.top - iconSize - offset,
            left: targetRect.left + targetRect.width / 2 - iconSize / 2
          };
        case "bottom":
          return {
            top: targetRect.top + targetRect.height + offset,
            left: targetRect.left + targetRect.width / 2 - iconSize / 2
          };
        case "left":
          return {
            top: targetRect.top + targetRect.height / 2 - iconSize / 2,
            left: targetRect.left - iconSize - offset
          };
        case "right":
        default:
          return {
            top: targetRect.top + targetRect.height / 2 - iconSize / 2,
            left: targetRect.left + targetRect.width + offset
          };
      }
    };

    const iconPos = getIconPosition();
    const finalIconPos = {
      top: iconPos.top + (tooltip.iconOffsetY ?? 0),
      left: iconPos.left + (tooltip.iconOffsetX ?? 0)
    };

    iconElements.push(
      <div
        key={`icon-${tooltip.id}`}
        data-icon-id={tooltip.id}
        className="onboarding-tooltip-layer__icon"
        style={
          {
            position: "fixed",
            top: finalIconPos.top,
            left: finalIconPos.left,
            width: iconSize,
            height: iconSize,
            cursor: "pointer",
            zIndex: 99999,
            "--kuika-svg-color": iconColor,
            "--kuika-svg-width": `${iconSize}px`,
            "--kuika-svg-height": `${iconSize}px`
          } as React.CSSProperties
        }
        onMouseEnter={(e) => handleIconMouseEnter(tooltip, e.currentTarget)}
        onMouseLeave={() => handleIconMouseLeave(tooltip)}
        onClick={(e) => handleIconClick(tooltip, e.currentTarget, e)}
        role="button"
        tabIndex={0}
        aria-label="Show tooltip"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleIconClick(tooltip, e.currentTarget, e as unknown as React.MouseEvent);
          }
        }}
      >
        {tooltip.icon?.startsWith("animated-") ? (
          <OnboardingAnimatedIcon
            type={tooltip.icon.replace("animated-", "") as AnimatedIconType}
            size={iconSize}
            color={iconColor}
          />
        ) : (
          <KuikaSvg name={tooltip.icon!} type="common" width={iconSize} height={iconSize} color={iconColor} />
        )}
      </div>
    );
  });

  return createPortal(
    <div className="onboarding-tooltip-layer">
      {iconElements}
      {tooltipElements}
    </div>,
    document.body
  );
};

export default GlobalOnboardingTooltipLayer;
